<script>
    import {beyondTopPlayers} from '../store';
    import {Toast} from "svelma";
    import {createEventDispatcher} from "svelte";
    import {HistoryEvent} from "../tactics.model";
    import {HistoryEventType} from "../history-events.enum";
    import {BeyondTopHistoryEvent} from "../beyondtop.model";
    import {ShufflePlayersHistoryEvent} from "../shuffle-players-history-event.model";
    import {Overlay} from "svelte-materialify";

    const dispatch = createEventDispatcher();

    export let rounds;
    export let beyondTopQuickGame;

    let scoreInputElement;
    let currentScore = 30;
    let currentRound = 0;
    let ranking;
    let playerScore;
    let gameFinished = false;
    let activePlayerIndex = 0;
    let currentRoundIndex = 0;
    let history = [];
    let showScores = false;
    let showAnimations = true;
    let overlayActive = false;
    let overlayText = "";
    let randomFile;

    function calculateAverage(activePlayer) {
        let historyItems = history.filter(historyItem => historyItem.playerName === activePlayer.name && historyItem.type === HistoryEventType.POINTS_ADDED);
        if (historyItems.length === 0) {
            return 0;
        }
        const sum = historyItems.reduce((accumulator, object) => {
            return accumulator + object.score;
        }, 0);

        return (sum / historyItems.length).toFixed(1);
    }

    function handleScore(scoreVal) {
        if (scoreVal == null) {
            scoreVal = 0;
        }

        if (scoreVal > 180) {
            Toast.create({message: 'More than maximum entered (180)!', type: 'is-danger'});
            return;
        }

        let activePlayer = $beyondTopPlayers[activePlayerIndex];
        let roundData = activePlayer.rounds.filter(item => item.round === parseInt(rounds[currentRound]))[0];
        let numberOfLives = activePlayer.lives;
        roundData.score = scoreVal;

        if (scoreVal <= currentScore) {
            roundData.hit = 'miss';
            numberOfLives--;
        } else {
            roundData.hit = 'clear';
        }

        let points = calculatePoints(scoreVal);
        let totalPoints = activePlayer.totalPoints + points;

        history = [...history, new BeyondTopHistoryEvent(HistoryEventType.POINTS_ADDED, activePlayer.name, activePlayerIndex, parseInt(rounds[currentRound]), scoreVal, currentScore, points, activePlayer.lives, activePlayer.eliminated)];

        activePlayer.rounds = [...activePlayer.rounds];
        let averageScore = calculateAverage(activePlayer);

        beyondTopPlayers.update(players => {
            players[activePlayerIndex].lives = numberOfLives;
            players[activePlayerIndex].totalPoints = totalPoints;
            players[activePlayerIndex].eliminated = (players[activePlayerIndex].lives === 0);
            players[activePlayerIndex].rounds = [...players[activePlayerIndex].rounds];
            players[activePlayerIndex].averageScore = averageScore;
            return [...players];
        });

        currentScore = scoreVal;

        if (showAnimations) {
            showAnimation(scoreVal);
        }

        checkWinner();
        if (!gameFinished) {
            nextPlayer();
        }

        if (scoreInputElement) {
            scoreInputElement.focus();
        }
    }

    function calculatePoints(scoreVal) {
        if (scoreVal <= currentScore) {
            return 0;
        }

        return Math.floor((scoreVal - currentScore) / 10);
    }

    function showAnimation(scoreVal) {
        let category = "";
        let nrOfAvailableAnimations = 0;

        if (scoreVal === 26 || scoreVal === 100) {
            category = "exact-score";
        } else if (scoreVal <= 10) {
            category = "fail";
            nrOfAvailableAnimations = 9;
        } else if (scoreVal > 10 && scoreVal < 30) {
            category = "score-low";
            nrOfAvailableAnimations = 8;
        } else if (scoreVal > 50 && scoreVal < 80) {
            category = "score-average";
            nrOfAvailableAnimations = 7;
        } else if (scoreVal > 80) {
            category = "score-high";
            nrOfAvailableAnimations = 7;
        }

        if (category === "exact-score") {
            randomFile = "images/gifs/" + scoreVal + ".webp";
            overlayActive = true;
        } else if (category !== "") {
            let randomIndex = Math.floor(Math.random() * nrOfAvailableAnimations) + 1;
            randomFile = "images/gifs/" + category + "/" + randomIndex + ".webp";
            overlayActive = true;
        }

        overlayText = currentScore;
        if (overlayActive) {
            setTimeout(() => {
                overlayActive = false;
            }, 3000);
        }
    }

    function nextPlayer() {
        if (activePlayerIndex === $beyondTopPlayers.length - 1) {
            shufflePlayers();

            activePlayerIndex = 0;
            if ($beyondTopPlayers[activePlayerIndex].eliminated) {
                nextPlayer();
            }
            currentRound = currentRound + 1;
        } else {
            activePlayerIndex = activePlayerIndex + 1;

            if ($beyondTopPlayers[activePlayerIndex].eliminated) {
                nextPlayer();
            }
        }
        playerScore = null;

        history = [...history, new HistoryEvent(HistoryEventType.NEXT_PLAYER, activePlayerIndex, 0)];
    }

    function shufflePlayers() {
        let activePlayers = [...$beyondTopPlayers].filter(player => !player.eliminated);
        let eliminatedPlayers = $beyondTopPlayers.filter(player => player.eliminated);

        if (activePlayers.length === 2) {
            $beyondTopPlayers = activePlayers.concat(eliminatedPlayers);
            return;
        }

        let playersToSort = [...$beyondTopPlayers].filter(player => !player.eliminated);
        let newOrder = playersToSort.sort(() => Math.random() - 0.5);

        let isSameOrder = JSON.stringify(activePlayers) === JSON.stringify(newOrder);
        let currentLastPlayer = activePlayers[activePlayers.length - 1]?.name;
        let newFirstPlayer = newOrder[0]?.name;
        if (!isSameOrder && currentLastPlayer !== newFirstPlayer) {
            $beyondTopPlayers = newOrder.concat(eliminatedPlayers);
            history = [...history, new ShufflePlayersHistoryEvent(HistoryEventType.SHUFFLE_PLAYERS, activePlayers)];
        } else {
            shufflePlayers();
        }
    }

    function checkWinner() {
        let activePlayers = $beyondTopPlayers.filter(player => !player.eliminated);
        let indexOfLastActivePlayer = $beyondTopPlayers.indexOf(activePlayers[activePlayers.length - 1]);

        if (($beyondTopPlayers.filter(player => !player.eliminated).length === 1)
            || ((activePlayerIndex === indexOfLastActivePlayer || activePlayerIndex === $beyondTopPlayers.length - 1) && currentRound === rounds.length - 1)) {
            ranking = $beyondTopPlayers.map((x) => x);
            ranking.forEach(player => {
                player.totalPoints = player.totalPoints + (player.lives * 3);
            });

            ranking = ranking.filter(player => !player.eliminated)
                .filter(player => player.totalPoints === Math.max.apply(Math, ranking.map(player => player.totalPoints)))
                .sort((player1, player2) => player1.totalPoints - player2.totalPoints)
                .reverse();

            gameFinished = true;
        }
    }

    function getRoundScore(player, round) {
        const roundEntry = player.rounds.find((r) => r.round === parseInt(round));
        if (!roundEntry || roundEntry.score == null) {
            return '';
        }
        return roundEntry.score;
    }

    function getRoundHit(player, round) {
        const roundEntry = player.rounds.find((r) => r.round === parseInt(round));
        return roundEntry ? roundEntry.hit : '';
    }

    function onEnterScore(e) {
        if ((e.key === 'Enter' || e.charCode === 13 || e.keyCode === 13) && !gameFinished) {
            e.preventDefault();
            handleScore(playerScore);
        }
    }

    function init(el) {
        scoreInputElement = el;
        el.focus();
    }

    function undo() {
        gameFinished = false;
        ranking = [];
        let lastHistoryItem = history[history.length - 1];

        if (!lastHistoryItem) {
            return; // No more history items
        }

        if (lastHistoryItem.type === HistoryEventType.SHUFFLE_PLAYERS) {
            $beyondTopPlayers = lastHistoryItem.players;
        }

        let player = $beyondTopPlayers[lastHistoryItem.playerIndex];

        if (lastHistoryItem.type === HistoryEventType.POINTS_ADDED) {
            currentScore = lastHistoryItem.previousScore;
            player.eliminated = false;
            player.totalPoints -= lastHistoryItem.points;
            player.lives = lastHistoryItem.lives;
            let segment = lastHistoryItem.segment;
            let roundSegment = player.rounds.filter(item => item.round === segment)[0];
            if (roundSegment) {
                roundSegment.hit = '';
                roundSegment.score = undefined;
            }
        }

        history.pop();
        history = history;
        if (player) {
            player.averageScore = calculateAverage(player);
        }

        beyondTopPlayers.update(beyondTopPlayers => {
            if (beyondTopPlayers[activePlayerIndex]) {
                beyondTopPlayers[activePlayerIndex].rounds = [...beyondTopPlayers[activePlayerIndex].rounds];
            }
            return [...beyondTopPlayers];
        });

        if (lastHistoryItem.type === HistoryEventType.NEXT_PLAYER) {
            if (activePlayerIndex - 1 < 0) {
                if (currentRound > 0) {
                    activePlayerIndex = $beyondTopPlayers.length - 1;
                    currentRound = currentRound - 1;
                }
            } else {
                activePlayerIndex = activePlayerIndex - 1;
            }

            undo();
        }

        if (scoreInputElement) {
            scoreInputElement.focus();
        }
    }

    function reset() {
        gameFinished = false;
        activePlayerIndex = 0;
        history = [];
        ranking = null;
        currentRound = 0;
        currentScore = 30;
        playerScore = null;

        dispatch("reset", {beyondTopQuickGame: beyondTopQuickGame, rounds: rounds});
        if (scoreInputElement) {
            scoreInputElement.focus();
        }
    }

    function isPlayerActive(player) {
        return $beyondTopPlayers[activePlayerIndex]?.name === player?.name && !gameFinished;
    }

    function isPlayerWinner(player) {
        return gameFinished && ranking && ranking.find(r => r.name === player.name) !== undefined;
    }

    function newGame() {
        dispatch('newGame');
    }
</script>

<Overlay active="{overlayActive}" opacity={1} on:click={() => {overlayActive = false;}}>
    <img src="{randomFile}" alt="" class="max-h-[70vh] object-contain rounded-2xl shadow-2xl" />
    <div class="overlayText font-headline mt-4">{overlayText}</div>
</Overlay>

<div class="beyondtop-screen w-full h-screen min-h-screen text-slate-900 font-sans antialiased flex flex-col p-3 sm:p-4 md:p-5 lg:p-6 box-border" style="background-color: #f0ece9;">
    <!-- BEGIN: TopControlsBar -->
    <header class="w-full max-w-[1600px] mx-auto bg-white rounded-2xl sm:rounded-3xl shadow-sm px-4 sm:px-6 py-3 mb-3 sm:mb-4 border border-black/5 flex flex-wrap items-center justify-between gap-4 shrink-0">
        <!-- Brand & Gameplay Toggles Group -->
        <div class="flex items-center gap-4 sm:gap-6 lg:gap-8 min-w-[240px]">
            <button class="inline-flex items-center transition-opacity hover:opacity-85 focus:outline-none" on:click={newGame} title="Yuma Home">
                <img alt="Yuma Logo" class="h-8 sm:h-9 w-auto object-contain" src="images/yuma.png" />
            </button>
            <!-- Quick Toggles -->
            <div class="flex flex-col gap-1.5 text-xs font-medium text-slate-500 border-l border-slate-200 pl-4 sm:pl-6">
                <!-- Show Scores Toggle -->
                <label class="inline-flex items-center gap-2 cursor-pointer select-none group">
                    <input type="checkbox" bind:checked={showScores} class="sr-only peer" />
                    <div class="w-7 h-4 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-[#EBA9FF] relative"></div>
                    <span class="text-xs text-slate-600 group-hover:text-slate-900 transition-colors">Show scores</span>
                </label>
                <!-- Show Animations Toggle -->
                <label class="inline-flex items-center gap-2 cursor-pointer select-none group">
                    <input type="checkbox" bind:checked={showAnimations} class="sr-only peer" />
                    <div class="w-7 h-4 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-[#EBA9FF] relative"></div>
                    <span class="text-xs text-slate-600 group-hover:text-slate-900 transition-colors">Show animations</span>
                </label>
            </div>
        </div>

        <!-- Active Input Center Console -->
        <div class="flex items-center justify-center flex-1 max-w-xl mx-auto order-last md:order-none w-full md:w-auto">
            <div class="flex items-center gap-2 w-full max-w-md bg-stone-100/80 p-1.5 rounded-2xl border border-slate-200 shadow-inner">
                <div class="relative flex-1">
                    <input
                        class="w-full h-11 bg-white text-slate-900 text-lg font-bold pl-4 pr-10 rounded-xl border border-slate-200 placeholder-slate-400 focus:border-[#21e467] focus:ring-2 focus:ring-[#21e467]/30 transition-all text-center tracking-wide outline-none disabled:opacity-50"
                        type="number"
                        min="0"
                        max="180"
                        placeholder="Score..."
                        bind:value={playerScore}
                        on:keypress={onEnterScore}
                        use:init
                        disabled={gameFinished}
                    />
                    <span class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-bold select-none pointer-events-none">PTS</span>
                </div>
                <button
                    class="h-11 inline-flex items-center justify-center gap-2 bg-[#EBA9FF] hover:bg-[#1bc658] active:scale-[0.98] text-[#005d46] font-bold px-5 sm:px-6 rounded-xl transition-all shadow-sm border border-emerald-500/20 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    type="button"
                    on:click={() => handleScore(playerScore)}
                    disabled={gameFinished}
                >
                    <span>Next player</span>
                    <span class="material-symbols-outlined text-[18px]">arrow_forward</span>
                </button>
            </div>
        </div>

        <!-- Right Quick Actions (Undo, Reset Round, Home) -->
        <div class="flex items-center justify-end gap-2.5 min-w-[200px] sm:min-w-[240px]">
            <!-- Undo / Previous Dart Button -->
            <button
                class="h-11 w-11 inline-flex items-center justify-center rounded-xl bg-pink-50 hover:bg-pink-100 text-rose-600 transition-colors border border-rose-200/50 cursor-pointer active:scale-95"
                title="Undo last score"
                type="button"
                on:click={undo}
            >
                <span class="material-symbols-outlined text-[20px]">undo</span>
            </button>
            <!-- New Round Button -->
            <button
                class="h-11 inline-flex items-center gap-2 bg-[#6434da] hover:bg-[#5229b5] text-white text-sm font-semibold px-4 rounded-xl transition-colors shadow-sm cursor-pointer active:scale-95"
                type="button"
                on:click={reset}
            >
                <span class="material-symbols-outlined text-[18px]">restart_alt</span>
                <span class="hidden sm:inline">New round</span>
            </button>
            <!-- Exit to Setup / Lobby -->
            <button
                class="h-11 w-11 inline-flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors border border-slate-200 cursor-pointer active:scale-95"
                title="Exit to Setup"
                type="button"
                on:click={newGame}
            >
                <span class="material-symbols-outlined text-[20px]">home</span>
            </button>
        </div>
    </header>
    <!-- END: TopControlsBar -->

    <!-- BEGIN: HeroTurnDisplay -->
    <section class="w-full max-w-[1600px] mx-auto mb-2 flex items-center justify-center">
        <div class="flex flex-col items-center">
            <div class="text-5xl md:text-6xl lg:text-9xl font-black text-[#005d46] tracking-tight drop-shadow-sm select-none font-headline">
                {currentScore}
            </div>
        </div>
    </section>
    <!-- END: HeroTurnDisplay -->

    <!-- BEGIN: MainScoreboardTable -->
    <main class="w-full max-w-[1600px] mx-auto flex-1 flex flex-col min-h-0">
        <div class="bg-white rounded-2xl sm:rounded-3xl shadow-md border border-black/5 overflow-hidden flex-1 flex flex-col min-h-0">
            <div class="overflow-auto flex-1 flex flex-col min-h-0">
                <table class="w-full h-full text-center border-collapse flex-1">
                    <thead>
                        <!-- Primary Player Header Row -->
                        <tr class="h-12 sm:h-14 text-white text-lg">
                            <!-- Game Mode Header -->
                            <th class="w-[20%] min-w-[160px] bg-[#005d46] px-5 py-2.5 align-middle text-left font-bold text-lg sm:text-xl tracking-tight border-r border-emerald-900/30 text-white" scope="col" style="text-align: left;">
                                <div class="flex items-center justify-start gap-2 sm:gap-3 text-left">
                                    <span class="material-symbols-outlined text-[#21e467] text-[24px] sm:text-[28px] shrink-0">trending_up</span>
                                    <div class="text-left">
                                        <div class="text-base sm:text-lg md:text-xl font-black font-headline tracking-tight text-white leading-tight">
                                            Beyond Top
                                        </div>
                                        <div class="text-[11px] sm:text-xs text-emerald-200/80 font-medium">
                                            Beat previous score
                                        </div>
                                    </div>
                                </div>
                            </th>

                            <!-- Player Headers -->
                            {#each $beyondTopPlayers as player, idx}
                                <th
                                    class="px-4 py-2.5 align-middle font-bold border-r transition-colors {
                                        isPlayerWinner(player)
                                            ? 'bg-[#21e467] text-[#005d46] border-emerald-600/40 shadow-sm'
                                            : isPlayerActive(player)
                                            ? 'bg-[#fff8e7] text-amber-950 border-amber-200/80 shadow-inner'
                                            : 'bg-[#005d46] text-white border-emerald-900/30'
                                    }"
                                    scope="col"
                                    style="width: {80 / $beyondTopPlayers.length}%"
                                >
                                    <div class="flex items-center justify-center gap-2 flex-wrap">
                                        {#if isPlayerActive(player) && !isPlayerWinner(player)}
                                            <span class="inline-block w-2.5 h-2.5 rounded-full bg-[#21e467] border-2 border-emerald-700 animate-pulse"></span>
                                        {/if}
                                        <span class="text-xl sm:text-2xl font-bold font-headline {player.eliminated ? 'line-through opacity-60 text-rose-300' : ''}">
                                            {player?.name}
                                        </span>
                                        {#if isPlayerWinner(player)}
                                            <span class="text-xl sm:text-2xl leading-none select-none inline-flex items-center">
                                                🏆
                                            </span>
                                        {/if}
                                    </div>
                                </th>
                            {/each}
                        </tr>
                    </thead>

                    <tbody class="divide-y divide-slate-200/70 text-base font-medium">
                        <!-- ROW: Lives / Levens -->
                        <tr class="compact-row bg-white/90 transition-colors">
                            <td class="bg-stone-50/90 text-right pr-6 py-1 align-middle font-bold text-slate-500 border-r border-slate-200 text-xs sm:text-sm uppercase tracking-wider font-headline">
                                Lives
                            </td>
                            {#each $beyondTopPlayers as player, idx}
                                <td class="py-1 align-middle border-r border-slate-200/50 {isPlayerActive(player) ? 'bg-[#fff8e7]/70 border-r border-amber-200/50' : ''}">
                                    <div class="flex items-center justify-center gap-1.5 flex-wrap px-2">
                                        {#if player.lives > 0}
                                            {#each Array(player.lives) as _, i}
                                                <svg class="w-4 h-4 sm:w-5 sm:h-5 fill-red-600 text-red-600 transition-transform hover:scale-110 shrink-0" style="fill: #dc2626;" viewBox="0 0 24 24">
                                                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"></path>
                                                </svg>
                                            {/each}
                                        {:else}
                                            <span class="text-xs font-bold text-rose-500 bg-rose-50 px-2 py-0.5 rounded-full">ELIMINATED</span>
                                        {/if}
                                    </div>
                                </td>
                            {/each}
                        </tr>

                        <!-- ROW: Average -->
                        <tr class="compact-row bg-stone-50/40">
                            <td class="bg-stone-50/90 text-right pr-6 py-1 align-middle font-bold text-slate-500 border-r border-slate-200 text-xs sm:text-sm uppercase tracking-wider font-headline">
                                Average
                            </td>
                            {#each $beyondTopPlayers as player, idx}
                                <td class="py-1 align-middle font-bold text-slate-700 text-sm sm:text-base border-r border-slate-200/50 {isPlayerActive(player) ? 'bg-[#fff8e7]/70 border-r border-amber-200/50 font-black text-amber-950' : ''}">
                                    {player?.averageScore || 0}
                                </td>
                            {/each}
                        </tr>

                        <!-- ROW: Points / Current Total -->
                        <tr class="compact-row bg-stone-100/60 border-b-2 border-slate-300">
                            <td class="bg-stone-50/90 text-right pr-6 py-1 align-middle font-bold text-slate-500 border-r border-slate-200 text-xs sm:text-sm uppercase tracking-wider font-headline">
                                Points
                            </td>
                            {#each $beyondTopPlayers as player, idx}
                                <td class="py-1 align-middle font-black text-base sm:text-lg border-r border-slate-200/50 {isPlayerActive(player) ? 'bg-[#fff8e7] text-emerald-800 border-r border-amber-200' : 'text-slate-900'}">
                                    {player?.totalPoints || 0}
                                </td>
                            {/each}
                        </tr>

                        <!-- ROUNDS -->
                        {#each rounds as round, rIndex}
                            <tr class="round-row transition-colors {
                                rIndex === currentRound
                                    ? 'bg-[#fef0cd]/30 hover:bg-[#fef0cd]/50'
                                    : rIndex % 2 === 0
                                    ? 'bg-white hover:bg-[#f5f3f0]'
                                    : 'bg-[#fbfaf8] hover:bg-[#f5f3f0]'
                            }">
                                <td class="round-label text-left pl-6 sm:pl-8 py-1 sm:py-2 align-middle font-bold border-r border-slate-200 font-headline {
                                    rIndex === currentRound ? 'text-emerald-900' : 'text-slate-600'
                                }">
                                    <div class="flex items-center gap-2 h-full min-h-[32px] sm:min-h-[38px]">
                                        {#if rIndex === currentRound}
                                            <span class="w-2.5 h-2.5 rounded-full bg-[#21e467] border border-emerald-600 shrink-0"></span>
                                        {/if}
                                        <span class="text-sm sm:text-base md:text-lg">Round {round}</span>
                                    </div>
                                </td>

                                {#each $beyondTopPlayers as player, pIdx}
                                    <td class="py-1 sm:py-2 align-middle text-center border-r border-slate-200/50 transition-all {
                                        rIndex === currentRound && pIdx === activePlayerIndex && !gameFinished
                                            ? 'bg-[#fef0cd]/70 border-r border-amber-300/80 font-bold text-amber-950'
                                            : rIndex === currentRound
                                            ? 'bg-[#fff8e7]/30 border-r border-amber-100'
                                            : ''
                                    }">
                                        <div class="flex items-center justify-center w-full h-full min-h-[32px] sm:min-h-[38px]">
                                            {#if rIndex === currentRound && pIdx === activePlayerIndex && !gameFinished}
                                                <div class="inline-flex items-center justify-center px-3 sm:px-4 py-1 rounded-lg border-2 border-dashed border-amber-400/80 text-amber-800 text-xs sm:text-sm font-semibold animate-pulse box-border">
                                                    Throwing...
                                                </div>
                                            {:else if getRoundHit(player, round) === 'clear'}
                                                <div class="inline-flex items-center justify-center gap-1.5 text-emerald-600 font-bold">
                                                    <span class="material-symbols-outlined text-[28px] sm:text-[32px] leading-none select-none">check_circle</span>
                                                    {#if showScores && getRoundScore(player, round) !== ''}
                                                        <span class="text-lg sm:text-xl md:text-2xl leading-none text-slate-800 font-bold">{getRoundScore(player, round)}</span>
                                                    {/if}
                                                </div>
                                            {:else if getRoundHit(player, round) === 'miss'}
                                                <div class="inline-flex items-center justify-center gap-1.5 text-rose-500 font-bold">
                                                    <span class="material-symbols-outlined text-[28px] sm:text-[32px] leading-none select-none">cancel</span>
                                                    {#if showScores && getRoundScore(player, round) !== ''}
                                                        <span class="text-lg sm:text-xl md:text-2xl leading-none text-rose-700 font-bold">{getRoundScore(player, round)}</span>
                                                    {/if}
                                                </div>
                                            {:else}
                                                <span class="text-slate-300 font-bold text-lg sm:text-xl leading-none select-none">-</span>
                                            {/if}
                                        </div>
                                    </td>
                                {/each}
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>

            <!-- Footer Info Strip inside table card -->
            <div class="px-5 sm:px-6 py-2.5 bg-stone-50 border-t border-slate-200/80 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500 font-medium shrink-0">
                <div class="flex items-center gap-3 sm:gap-4">
                    <span class="inline-flex items-center gap-1.5">
                        <span class="w-2 h-2 rounded-full bg-[#21e467]"></span>
                        <strong class="text-slate-700">Beyond Top rules:</strong> Beat target score ({currentScore}) or lose 1 life
                    </span>
                    <span class="text-slate-300 hidden sm:inline">|</span>
                    <span class="hidden sm:inline">Match length: {rounds.length} Rounds</span>
                </div>
                <div class="flex items-center gap-3">
                    <span class="text-slate-400">Press <kbd class="px-1.5 py-0.5 bg-white border border-slate-300 rounded text-[11px] text-slate-700 font-mono shadow-xs font-semibold">ENTER</kbd> to submit score</span>
                </div>
            </div>
        </div>
    </main>
    <!-- END: MainScoreboardTable -->
</div>

<style>
    /* Custom styles for Beyond Top scoreboard */
    .beyondtop-screen {
        font-family: 'Space Grotesk', 'Inter', sans-serif;
    }

    .beyondtop-screen table {
        table-layout: fixed;
    }

    .beyondtop-screen table th,
    .beyondtop-screen table td {
        vertical-align: middle !important;
    }

    .beyondtop-screen .compact-row {
        height: 1px;
    }

    .beyondtop-screen .round-row {
        height: 1%;
    }

    .overlayText {
        color: white;
        text-align: center;
        font-size: 6rem;
        font-weight: 900;
        text-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
    }

    input[type=number]::-webkit-inner-spin-button,
    input[type=number]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    input[type=number] {
        -moz-appearance: textfield;
    }
</style>
